
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useProgressAnimation } from '@/hooks/useProgressAnimation';

interface ProgressBarProps {
  value: number;
  max: number;
  label?: string;
  showValue?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'accent';
  isRecycledItem?: boolean;
}

const ProgressBar = ({
  value,
  max,
  label,
  showValue = false,
  className,
  size = 'md',
  color = 'primary',
  isRecycledItem = false
}: ProgressBarProps) => {
  // Calculate percentage
  const rawPercentage = Math.min((value / max) * 100, 100);
  
  // Use our reusable progress animation hook
  const { animatedProgress } = useProgressAnimation({
    progress: rawPercentage,
    duration: 800,
    preventBackwards: true,
    skipIfRecycled: true,
    isRecycled: isRecycledItem
  });
  
  const sizeClasses = {
    sm: 'h-2',
    md: 'h-4',
    lg: 'h-6'
  };

  // Color maps
  const colorMap = {
    primary: 'var(--primary)',
    secondary: 'var(--secondary)',
    accent: 'var(--accent)'
  };

  return (
    <div className={cn("w-full space-y-1.5", className)}>
      {(label || showValue) && (
        <div className="flex justify-between items-center">
          {label && <p className="text-sm font-medium text-muted-foreground">{label}</p>}
          {showValue && (
            <p className="text-sm font-medium text-foreground">
              {value} / {max}
              <span className={cn(
                "text-xs text-muted-foreground ml-1",
                animatedProgress > 95 && "text-primary font-semibold"
              )}>
                ({Math.round(animatedProgress)}%)
              </span>
            </p>
          )}
        </div>
      )}
      <div className="relative w-full overflow-hidden rounded-full bg-muted/50">
        <Progress
          value={animatedProgress}
          className={cn(
            "transition-all duration-500", 
            sizeClasses[size],
            animatedProgress === 100 && "animate-pulse"
          )}
          style={{ 
            "--progress-indicator-color": colorMap[color]
          } as React.CSSProperties}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
